import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { formatFechaHora } from "@/lib/format";
import { RASTREO } from "@/constants/testIds";

export const Gallery = ({ fotos }) => {
  const [abierta, setAbierta] = useState(null); // índice

  useEffect(() => {
    const onKey = (e) => {
      if (abierta === null) return;
      if (e.key === "Escape") setAbierta(null);
      if (e.key === "ArrowRight") setAbierta((i) => (i + 1) % fotos.length);
      if (e.key === "ArrowLeft") setAbierta((i) => (i - 1 + fotos.length) % fotos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [abierta, fotos.length]);

  if (!fotos || fotos.length === 0) return null;

  const foto = abierta !== null ? fotos[abierta] : null;

  return (
    <div data-testid={RASTREO.gallery}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {fotos.map((f, i) => (
          <button
            key={f.id}
            data-testid={RASTREO.galleryPhoto}
            onClick={() => setAbierta(i)}
            className="group relative overflow-hidden rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-[var(--tc-surface)] focus:outline-none focus:ring-2 focus:ring-[var(--tc-accent)]"
          >
            <div className="aspect-[4/3] w-full overflow-hidden">
              <img
                src={mediaUrl(f.url)}
                alt={f.leyenda || "Foto de la unidad"}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
              />
            </div>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2.5 pb-2 pt-6 text-left">
              {f.leyenda && (
                <p className="text-xs font-medium text-white">{f.leyenda}</p>
              )}
              <p className="text-[10px] text-white/80">{formatFechaHora(f.tomada_en)}</p>
            </div>
          </button>
        ))}
      </div>

      {foto && (
        <div
          data-testid={RASTREO.lightbox}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 p-4 tc-fade-up"
          onClick={() => setAbierta(null)}
        >
          <button
            data-testid={RASTREO.lightboxClose}
            onClick={() => setAbierta(null)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20"
            aria-label="Cerrar"
          >
            <X size={22} />
          </button>

          {fotos.length > 1 && (
            <>
              <span
                data-testid={RASTREO.lightboxCounter}
                className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium tabular-nums text-white"
              >
                {abierta + 1} / {fotos.length}
              </span>
              <button
                data-testid={RASTREO.lightboxPrev}
                onClick={(e) => { e.stopPropagation(); setAbierta((i) => (i - 1 + fotos.length) % fotos.length); }}
                className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20"
                aria-label="Anterior"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                data-testid={RASTREO.lightboxNext}
                onClick={(e) => { e.stopPropagation(); setAbierta((i) => (i + 1) % fotos.length); }}
                className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20"
                aria-label="Siguiente"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          <img
            src={mediaUrl(foto.url)}
            alt={foto.leyenda || "Foto de la unidad"}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[80vh] max-w-full rounded-[var(--tc-radius)] object-contain"
          />
          <div className="mt-4 text-center">
            {foto.leyenda && <p className="text-sm font-medium text-white">{foto.leyenda}</p>}
            <p className="text-xs text-white/70">{formatFechaHora(foto.tomada_en)}</p>
          </div>
        </div>
      )}
    </div>
  );
};
